import * as THREE from 'three';
import { FLOOR } from './kli.js';

/*
 * Kli's wardrobe. Every accessory is built from primitives in the KLIENTO palette
 * (one strong blue, ink, white), so nothing here needs a texture file.
 *
 * Each factory takes a Kli and returns { visible, update(dt, t, kli), poke() }.
 * Head-worn pieces go on kli.head (they turn with the face); props go on kli.stage.
 * Body space: ellipsoid 1 x 0.88 x 1 plus ~0.2 of fur, facing +z.
 */

export const C = { blue: '#2DACDF', blue300: '#7DCBEB', blue700: '#1E7293', ink: '#16242E', white: '#FFFFFF', mist: '#EEF3F5' };

export const mat = (color, o = {}) => new THREE.MeshPhysicalMaterial({
  color, roughness: o.rough ?? 0.38, metalness: o.metal ?? 0, clearcoat: o.coat ?? 0.5, clearcoatRoughness: 0.25,
  envMapIntensity: o.env ?? 0.8, side: o.side ?? THREE.FrontSide,
});
export const M = {
  blue: mat(C.blue), blueDeep: mat(C.blue700), ink: mat(C.ink, { rough: 0.45, env: 0.4 }),
  white: mat(C.white, { rough: 0.42 }), mist: mat(C.mist), metal: mat('#C9D3D9', { rough: 0.25, metal: 0.9 }),
  glass: new THREE.MeshPhysicalMaterial({ color: '#E0F3FA', transparent: true, opacity: 0.22, roughness: 0.05, clearcoat: 1, depthWrite: false }),
};
export const mesh = (geo, m, pos, rot) => {
  const o = new THREE.Mesh(geo, m);
  if (pos) o.position.set(...pos);
  if (rot) o.rotation.set(...rot);
  return o;
};

/** Wraps the groups an accessory adds so they can be shown/hidden together. */
export function piece(groups, update, poke) {
  let vis = true;
  return {
    get visible() { return vis; },
    set visible(v) { vis = v; groups.forEach((g) => { g.visible = v; }); },
    update(dt, t, kli) { if (vis && update) update(dt, t, kli); },
    poke() { if (vis && poke) poke(); },
  };
}

export function canvasTexture(w, h, draw) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const g = c.getContext('2d');
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  const redraw = (...a) => { g.clearRect(0, 0, w, h); draw(g, w, h, ...a); tex.needsUpdate = true; };
  redraw();
  return { tex, redraw };
}
export function roundRect(g, x, y, w, h, r) { g.beginPath(); g.roundRect(x, y, w, h, r); }
export const FONT = 'Rubik, "IBM Plex Sans Hebrew", system-ui, sans-serif';

/* Typing bubble -------------------------------------------------------------- */
// The bubble itself comes from the glb (makeKli({ bubble: true })); this only toggles it.
export function bubble(kli) { return piece([kli.bubble]); }

/* Headset: the smart switchboard ------------------------------------------- */
export function headset(kli) {
  const g = new THREE.Group();
  g.position.y = 0.05;
  g.rotation.x = -0.12;
  g.add(mesh(new THREE.TorusGeometry(1.1, 0.06, 14, 64, Math.PI), M.ink));
  for (const s of [-1, 1]) {
    g.add(mesh(new THREE.CylinderGeometry(0.29, 0.29, 0.2, 32), M.ink, [s * 1.12, 0, 0], [0, 0, Math.PI / 2]));
    g.add(mesh(new THREE.TorusGeometry(0.22, 0.075, 12, 32), M.blue, [s * 1.01, 0, 0], [0, Math.PI / 2, 0]));
    g.add(mesh(new THREE.CylinderGeometry(0.19, 0.19, 0.05, 32), M.blue, [s * 1.23, 0, 0], [0, 0, Math.PI / 2]));
  }
  const boom = new THREE.CatmullRomCurve3([
    new THREE.Vector3(1.12, -0.08, 0.14), new THREE.Vector3(1.0, -0.42, 0.62), new THREE.Vector3(0.42, -0.52, 1.02),
  ]);
  g.add(mesh(new THREE.TubeGeometry(boom, 24, 0.028, 8), M.ink));
  g.add(mesh(new THREE.SphereGeometry(0.085, 20, 14), M.blue, [0.42, -0.52, 1.02]));
  const led = mesh(new THREE.SphereGeometry(0.035, 12, 8), new THREE.MeshBasicMaterial({ color: C.blue300, toneMapped: false }), [-1.26, 0.12, 0.12]);
  g.add(led);

  // Sound waves leaving the far ear cup: rings that grow and fade
  const waves = [0, 1, 2].map(() => {
    const m = mesh(new THREE.TorusGeometry(0.26, 0.022, 8, 40),
      new THREE.MeshBasicMaterial({ color: C.blue, transparent: true, toneMapped: false, depthWrite: false }),
      [-1.3, 0, 0], [0, Math.PI / 2, 0]);
    g.add(m);
    return m;
  });
  kli.head.add(g);
  let boost = 0;
  return piece([g], (dt, t) => {
    boost = Math.max(0, boost - dt);
    waves.forEach((w, i) => {
      const p = ((t * (0.6 + boost) + i / 3) % 1);
      w.position.x = -1.3 - p * 0.55;
      w.scale.setScalar(0.7 + p * 1.1);
      w.material.opacity = (1 - p) * 0.7;
    });
    led.visible = Math.sin(t * 5) > -0.3 || boost > 0;
  }, () => { boost = 1.5; });
}

/* Phone with a live WhatsApp-style chat: the WhatsApp agent ------------------ */
const CHAT = [
  { me: true, text: 'היי, אפשר לקבוע תור למחר?' },
  { me: false, text: 'בטח! יש 10:00 או 13:30' },
  { me: true, text: '13:30 מעולה' },
  { me: false, text: 'קבעתי ✓ תזכורת תגיע מחר' },
];
export function phone(kli, { x = 1.55, y = 0.05, z = 0.25, ry = -0.38 } = {}) {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  g.rotation.set(-0.05, ry, 0.06);
  const shape = new THREE.Shape();
  {
    const w = 0.8, h = 1.56, r = 0.12, x0 = -0.4, y0 = -0.78;
    shape.moveTo(x0 + r, y0); shape.lineTo(x0 + w - r, y0); shape.quadraticCurveTo(x0 + w, y0, x0 + w, y0 + r);
    shape.lineTo(x0 + w, y0 + h - r); shape.quadraticCurveTo(x0 + w, y0 + h, x0 + w - r, y0 + h);
    shape.lineTo(x0 + r, y0 + h); shape.quadraticCurveTo(x0, y0 + h, x0, y0 + h - r);
    shape.lineTo(x0, y0 + r); shape.quadraticCurveTo(x0, y0, x0 + r, y0);
  }
  const body = new THREE.ExtrudeGeometry(shape, { depth: 0.06, bevelEnabled: true, bevelSize: 0.02, bevelThickness: 0.02, bevelSegments: 3, curveSegments: 10 });
  body.translate(0, 0, -0.03);
  g.add(mesh(body, M.ink));

  let shown = 1, typing = false;
  const screen = canvasTexture(360, 720, (c, w, h) => {
    c.fillStyle = '#F7FAFB'; roundRect(c, 0, 0, w, h, 40); c.fill();
    c.fillStyle = C.blue; roundRect(c, 0, 0, w, 104, [40, 40, 0, 0]); c.fill();
    c.direction = 'rtl'; c.textAlign = 'right';
    c.fillStyle = '#fff'; c.font = `800 30px ${FONT}`; c.fillText('KLIENTO', w - 92, 52);
    c.font = `400 21px ${FONT}`; c.fillText(typing ? 'מקליד/ה…' : 'מחובר/ת', w - 92, 82);
    c.beginPath(); c.arc(w - 50, 60, 26, 0, Math.PI * 2); c.fillStyle = '#fff'; c.fill();
    c.beginPath(); c.arc(w - 50, 60, 15, 0, Math.PI * 2); c.lineWidth = 7; c.strokeStyle = C.blue; c.stroke();
    let yy = 140;
    c.font = `400 22px ${FONT}`;
    for (const m of CHAT.slice(0, shown)) {
      const tw = Math.min(c.measureText(m.text).width + 36, w - 70);
      const bx = m.me ? w - tw - 18 : 18;   // the customer on the right (RTL), the agent on the left
      c.fillStyle = m.me ? '#fff' : '#E0F3FA';
      roundRect(c, bx, yy, tw, 54, 18); c.fill();
      if (m.me) { c.strokeStyle = '#E6ECEF'; c.lineWidth = 2; c.stroke(); }
      c.fillStyle = C.ink; c.fillText(m.text, bx + tw - 18, yy + 35);
      yy += 72;
    }
    if (typing) {
      c.fillStyle = '#E0F3FA'; roundRect(c, 18, yy, 96, 50, 18); c.fill();
      [0, 1, 2].forEach((i) => { c.beginPath(); c.arc(44 + i * 22, yy + 25, 7, 0, Math.PI * 2); c.fillStyle = C.blue; c.fill(); });
    }
    c.fillStyle = '#fff'; roundRect(c, 18, h - 82, w - 36, 58, 29); c.fill();
    c.strokeStyle = '#E6ECEF'; c.lineWidth = 2; c.stroke();
  });
  const scr = mesh(new THREE.PlaneGeometry(0.72, 1.44), new THREE.MeshBasicMaterial({ map: screen.tex, toneMapped: false, transparent: true }), [0, 0, 0.056]);
  g.add(scr);
  kli.stage.add(g);
  document.fonts?.ready.then(() => screen.redraw());

  // The conversation plays on a loop; the agent "types" before each of its replies
  const FRAMES = [[1], [1, true], [2], [3], [3, true], [4], [4]];
  let clock = 0, step = 0;
  const advance = () => {
    step = (step + 1) % FRAMES.length;
    [shown, typing = false] = FRAMES[step];
    screen.redraw();
  };
  return piece([g], (dt, t) => {
    g.position.y = y + Math.sin(t * 1.5) * 0.05;
    g.rotation.z = 0.06 + Math.sin(t * 1.1) * 0.03;
    clock += dt;
    if (clock > 1.3) { clock = 0; advance(); }
  }, () => { clock = 2; });
}

/* Glasses + contact cards: the CRM ----------------------------------------- */
export function glasses(kli) {
  const g = new THREE.Group();
  for (const s of [-1, 1]) {
    g.add(mesh(new THREE.TorusGeometry(0.21, 0.032, 10, 40), M.ink, [s * 0.37, 0.22, 1.13]));
    g.add(mesh(new THREE.CircleGeometry(0.2, 32), M.glass, [s * 0.37, 0.22, 1.12]));
    const from = new THREE.Vector3(s * 0.58, 0.24, 1.06), to = new THREE.Vector3(s * 1.0, 0.26, 0.22);
    const len = from.distanceTo(to);
    const arm = mesh(new THREE.CylinderGeometry(0.022, 0.022, len, 8), M.ink);
    arm.position.copy(from).lerp(to, 0.5);
    arm.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), to.clone().sub(from).normalize());
    g.add(arm);
  }
  g.add(mesh(new THREE.TorusGeometry(0.09, 0.026, 8, 20, Math.PI), M.ink, [0, 0.25, 1.15]));
  kli.head.add(g);
  let nudge = 0;
  return piece([g], (dt) => {
    nudge = Math.max(0, nudge - dt * 3);
    g.position.y = Math.sin(nudge * Math.PI) * 0.06;   // pushes the glasses up its nose
  }, () => { nudge = 1; });
}

const PEOPLE = [['דנה כהן', 'ליד חדש', C.blue], ['יוסי לוי', 'פגישה מחר', C.blue700], ['מאיה ש.', 'לקוחה פעילה', C.ink]];
export function contactCards(kli, { x = 1.6, y = 0.25, z = 0 } = {}) {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  g.rotation.y = -0.3;
  const cards = PEOPLE.map(([name, tag, color], i) => {
    const { tex } = canvasTexture(400, 260, (c, w, h) => {
      c.fillStyle = '#fff'; roundRect(c, 4, 4, w - 8, h - 8, 28); c.fill();
      c.strokeStyle = '#E6ECEF'; c.lineWidth = 4; c.stroke();
      c.beginPath(); c.arc(w - 70, 72, 38, 0, Math.PI * 2); c.fillStyle = '#E0F3FA'; c.fill();
      c.beginPath(); c.arc(w - 70, 62, 13, 0, Math.PI * 2); c.fillStyle = color; c.fill();
      c.beginPath(); c.arc(w - 70, 98, 22, Math.PI, 0); c.fill();
      c.direction = 'rtl'; c.textAlign = 'right'; c.fillStyle = C.ink;
      c.font = `700 34px ${FONT}`; c.fillText(name, w - 128, 70);
      c.font = `400 24px ${FONT}`; c.fillStyle = '#5C6B75'; c.fillText('054-•••-••••', w - 128, 104);
      c.fillStyle = '#EEF3F5'; roundRect(c, 32, 150, w - 64, 14, 7); c.fill(); roundRect(c, 32, 178, (w - 64) * 0.6, 14, 7); c.fill();
      c.font = `500 22px ${FONT}`; const tw = c.measureText(tag).width + 32;
      c.fillStyle = color; roundRect(c, 32, 40, tw, 40, 20); c.fill();
      c.fillStyle = '#fff'; c.textAlign = 'center'; c.fillText(tag, 32 + tw / 2, 67);
    });
    const m = mesh(new THREE.PlaneGeometry(1.0, 0.65), new THREE.MeshBasicMaterial({ map: tex, transparent: true, toneMapped: false, side: THREE.DoubleSide }));
    g.add(m);
    return m;
  });
  kli.stage.add(g);
  document.fonts?.ready.then(() => cards.forEach((c) => { c.material.map.needsUpdate = true; }));
  let order = [0, 1, 2], shuffle = 0;
  return piece([g], (dt, t) => {
    g.position.y = y + Math.sin(t * 1.3) * 0.05;
    if (shuffle > 0) { shuffle = Math.max(0, shuffle - dt * 2.2); if (shuffle === 0) order.push(order.shift()); }
    order.forEach((ci, slot) => {
      const c = cards[ci];
      const lift = slot === 0 && shuffle > 0 ? Math.sin(shuffle * Math.PI) : 0;
      c.position.set(-slot * 0.1 + lift * 0.7, -slot * 0.16 + lift * 0.3, -slot * 0.08);
      c.rotation.z = slot * 0.08 + Math.sin(t * 0.9 + slot) * 0.02 - lift * 0.3;
    });
  }, () => { if (!shuffle) shuffle = 1; });
}

/* Gears: automations -------------------------------------------------------- */
function gearGeometry(r, teeth, depth = 0.14) {
  const s = new THREE.Shape();
  const ri = r * 0.8, step = (Math.PI * 2) / teeth;
  for (let i = 0; i < teeth; i++) {
    const a = i * step;
    const pts = [[ri, a], [r, a + step * 0.15], [r, a + step * 0.45], [ri, a + step * 0.6]];
    pts.forEach(([rr, aa], j) => { const p = [Math.cos(aa) * rr, Math.sin(aa) * rr]; (i || j) ? s.lineTo(...p) : s.moveTo(...p); });
  }
  const hole = new THREE.Path(); hole.absarc(0, 0, r * 0.28, 0, Math.PI * 2, true);
  s.holes.push(hole);
  const geo = new THREE.ExtrudeGeometry(s, { depth, bevelEnabled: true, bevelSize: 0.015, bevelThickness: 0.015, bevelSegments: 2 });
  geo.translate(0, 0, -depth / 2);
  return geo;
}
export function gears(kli, { x = 1.3, y = 0.85, z = -0.1 } = {}) {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  g.rotation.y = -0.35;
  const big = mesh(gearGeometry(0.46, 12), M.blue);
  const small = mesh(gearGeometry(0.3, 8), M.ink, [0.5, -0.5, 0.02]);
  const tiny = mesh(gearGeometry(0.22, 6), M.white, [-0.38, -0.56, -0.02]);
  g.add(big, small, tiny);
  kli.stage.add(g);
  let speed = 0.6;
  return piece([g], (dt, t) => {
    speed += (0.6 - speed) * dt * 1.2;
    big.rotation.z += dt * speed;
    small.rotation.z -= dt * speed * 12 / 8;
    tiny.rotation.z -= dt * speed * 12 / 6;
    g.position.y = y + Math.sin(t * 1.2) * 0.05;
  }, () => { speed = 6; });
}

/* Linked rings: integrations (two O marks, locked together) ---------------- */
export function rings(kli, { x = 1.4, y = 0.55, z = 0 } = {}) {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  const geo = new THREE.TorusGeometry(0.36, 0.1, 18, 48);
  const a = mesh(geo, M.blue, [-0.2, 0, 0]);
  const b = mesh(geo, M.ink, [0.2, 0, 0], [Math.PI / 2, 0, 0]);
  g.add(a, b);
  // data packets travelling around both rings
  const dotM = new THREE.MeshBasicMaterial({ color: C.white, toneMapped: false });
  const dots = [0, 1, 2, 3].map(() => { const d = mesh(new THREE.SphereGeometry(0.045, 10, 8), dotM); g.add(d); return d; });
  kli.stage.add(g);
  let spin = 0.4;
  return piece([g], (dt, t) => {
    spin += (0.4 - spin) * dt * 1.5;
    g.rotation.y += dt * spin;
    g.rotation.x = Math.sin(t * 0.8) * 0.25;
    g.position.y = y + Math.sin(t * 1.4) * 0.05;
    dots.forEach((d, i) => {
      const ang = t * 1.6 + (i % 2) * Math.PI;
      if (i < 2) d.position.set(-0.2 + Math.cos(ang) * 0.36, Math.sin(ang) * 0.36, 0.11);
      else d.position.set(0.2 + Math.cos(-ang) * 0.36, 0.11, Math.sin(-ang) * 0.36);
    });
  }, () => { spin = 9; });
}

/* Hard hat + wrench: custom systems ----------------------------------------- */
export function hardhat(kli) {
  const g = new THREE.Group();
  const dome = new THREE.Group();
  dome.position.y = 0.5;
  dome.scale.set(1.04, 0.64, 1.04);
  dome.add(mesh(new THREE.SphereGeometry(1.0, 48, 20, 0, Math.PI * 2, 0, Math.PI / 2), M.white));
  dome.add(mesh(new THREE.TorusGeometry(1.0, 0.075, 10, 48, Math.PI), M.blue, null, [0, Math.PI / 2, 0]));
  g.add(dome);
  const brim = mesh(new THREE.CylinderGeometry(1.14, 1.14, 0.05, 48), M.white, [0, 0.5, 0.1]);
  brim.scale.z = 1.05;
  g.add(brim);
  // the O mark as a badge on the front
  g.add(mesh(new THREE.TorusGeometry(0.11, 0.04, 10, 24), M.blue, [0.42, 0.78, 0.8], [-0.55, 0.45, 0]));
  kli.head.add(g);
  let tip = 0;
  return piece([g], (dt) => {
    tip = Math.max(0, tip - dt * 2.5);
    g.rotation.x = -Math.sin(tip * Math.PI) * 0.18;   // tips the hat
  }, () => { tip = 1; });
}
export function wrench(kli, { x = 1.35, y = 0.15, z = 0.3 } = {}) {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  const w = new THREE.Group();
  w.add(mesh(new THREE.BoxGeometry(0.13, 0.8, 0.07), M.blue));
  w.add(mesh(new THREE.TorusGeometry(0.15, 0.065, 10, 24, Math.PI * 1.55), M.blue, [0, 0.5, 0], [0, 0, -Math.PI * 0.27 + Math.PI / 2]));
  w.add(mesh(new THREE.TorusGeometry(0.1, 0.05, 10, 24), M.blue, [0, -0.47, 0]));
  w.rotation.z = -0.5;
  g.add(w);
  kli.stage.add(g);
  let spin = 0;
  return piece([g], (dt, t) => {
    spin = Math.max(0, spin - dt * 1.4);
    w.rotation.z = -0.5 + Math.sin(t * 2.4) * 0.22 + (1 - Math.cos(spin * Math.PI)) * Math.PI;
    g.position.y = y + Math.sin(t * 1.3) * 0.05;
  }, () => { spin = 1; });
}

/* Magnifier: discovery ------------------------------------------------------- */
export function magnifier(kli) {
  const g = new THREE.Group();
  g.add(mesh(new THREE.TorusGeometry(0.32, 0.055, 14, 48), M.blue));
  g.add(mesh(new THREE.CircleGeometry(0.31, 40), M.glass));
  const handle = mesh(new THREE.CylinderGeometry(0.055, 0.065, 0.62, 16), M.ink, [0.4, -0.4, 0], [0, 0, Math.PI / 4]);
  g.add(handle);
  kli.stage.add(g);
  let zoom = 0;
  return piece([g], (dt, t) => {
    zoom = Math.max(0, zoom - dt * 1.5);
    // drifts in front of one eye, then the other, then out to the side
    g.position.set(0.65 + Math.sin(t * 0.7) * 0.55, 0.25 + Math.sin(t * 1.4) * 0.12, 1.45 + Math.sin(zoom * Math.PI) * 0.3);
    g.rotation.z = Math.sin(t * 0.7) * 0.2;
  }, () => { zoom = 1; });
}

/* Laptop: building ---------------------------------------------------------- */
export function laptop(kli, { x = 1.25, z = 0.75, ry = -2.3 } = {}) {
  const g = new THREE.Group();
  g.position.set(x, FLOOR, z);
  g.rotation.y = ry;
  g.add(mesh(new THREE.BoxGeometry(1.0, 0.05, 0.66), M.mist, [0, 0.025, 0]));
  const lid = new THREE.Group();
  lid.position.set(0, 0.05, -0.33);
  lid.rotation.x = -0.28;
  lid.add(mesh(new THREE.BoxGeometry(1.0, 0.66, 0.035), M.mist, [0, 0.33, 0]));
  let lines = 3, blink = true;
  const code = canvasTexture(400, 256, (c, w, h) => {
    c.fillStyle = C.ink; c.fillRect(0, 0, w, h);
    const cols = [C.blue, C.blue300, '#93A1AB', '#fff'];
    for (let i = 0; i < lines; i++) {
      const indent = [0, 1, 2, 2, 1, 2, 0][i % 7] * 26;
      const len = 80 + ((i * 97) % 180);
      c.fillStyle = cols[i % 4]; roundRect(c, 24 + indent, 22 + i * 30, len, 12, 6); c.fill();
    }
    if (blink) { c.fillStyle = '#fff'; c.fillRect(24 + 20, 22 + lines * 30 - 4, 10, 20); }
  });
  lid.add(mesh(new THREE.PlaneGeometry(0.92, 0.58), new THREE.MeshBasicMaterial({ map: code.tex, toneMapped: false }), [0, 0.33, 0.019]));
  // the O mark on the back of the lid
  lid.add(mesh(new THREE.TorusGeometry(0.1, 0.035, 10, 24), M.blue, [0, 0.33, -0.022]));
  g.add(lid);
  kli.stage.add(g);
  let clock = 0;
  return piece([g], (dt) => {
    clock += dt;
    if (clock > 0.45) { clock = 0; blink = !blink; if (blink) lines = lines >= 7 ? 1 : lines + 1; code.redraw(); }
  }, () => { lines = 7; code.redraw(); });
}

/* Party hat + confetti: launch ---------------------------------------------- */
export function partyHat(kli) {
  const { tex } = canvasTexture(256, 256, (c, w, h) => {
    c.fillStyle = C.blue; c.fillRect(0, 0, w, h);
    c.fillStyle = '#fff';
    for (let i = -4; i < 8; i++) { c.save(); c.translate(i * 48, 0); c.transform(1, 0, -0.6, 1, 0, 0); c.fillRect(0, 0, 22, h); c.restore(); }
  });
  const g = new THREE.Group();
  g.position.set(-0.22, 0.78, 0.05);
  g.rotation.z = 0.28;
  const cone = mesh(new THREE.ConeGeometry(0.4, 0.95, 40, 1, true), new THREE.MeshPhysicalMaterial({ map: tex, roughness: 0.45, clearcoat: 0.4, side: THREE.DoubleSide }), [0, 0.47, 0]);
  g.add(cone);
  g.add(mesh(new THREE.SphereGeometry(0.12, 20, 14), M.white, [0, 0.97, 0]));
  kli.head.add(g);
  let wob = 0;
  return piece([g], (dt, t) => {
    wob = Math.max(0, wob - dt * 1.6);
    g.rotation.z = 0.28 + Math.sin(t * 9) * 0.25 * wob;
  }, () => { wob = 1; });
}
export function confetti(kli, { count = 70, radius = 1.8, top = 2.4 } = {}) {
  const palette = [C.blue, C.blue300, C.blue700, C.ink, '#ffffff'].map((c) => new THREE.Color(c));
  const inst = new THREE.InstancedMesh(new THREE.PlaneGeometry(0.07, 0.12),
    new THREE.MeshBasicMaterial({ side: THREE.DoubleSide, toneMapped: false }), count);
  const ps = Array.from({ length: count }, (_, i) => {
    inst.setColorAt(i, palette[i % palette.length]);
    return { x: (Math.random() - 0.5) * radius * 2, y: FLOOR + Math.random() * (top - FLOOR), z: (Math.random() - 0.5) * radius,
      vy: -0.3 - Math.random() * 0.4, vx: 0, rx: Math.random() * 6, ry: Math.random() * 6, spin: 2 + Math.random() * 4 };
  });
  kli.stage.add(inst);
  const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler(), p = new THREE.Vector3(), one = new THREE.Vector3(1, 1, 1);
  let burst = 0;
  const api = piece([inst], (dt, t) => {
    burst = Math.max(0, burst - dt);
    ps.forEach((c, i) => {
      c.vy = Math.max(c.vy - dt * 1.2, -0.5);   // flutters down slowly
      c.y += c.vy * dt; c.x += c.vx * dt; c.vx *= 0.98;
      if (c.y < FLOOR) { c.y = top; c.x = (Math.random() - 0.5) * radius * 2; c.vy = -0.3; }
      c.rx += dt * c.spin; c.ry += dt * c.spin * 0.7;
      e.set(c.rx, c.ry, t); q.setFromEuler(e); p.set(c.x + Math.sin(t * 2 + i) * 0.04, c.y, c.z);
      inst.setMatrixAt(i, m4.compose(p, q, one));
    });
    inst.instanceMatrix.needsUpdate = true;
  }, () => {
    ps.forEach((c) => { c.y = 0.6; c.x = (Math.random() - 0.5) * 0.4; c.vy = 2 + Math.random() * 2.2; c.vx = (Math.random() - 0.5) * 4; });
  });
  return api;
}

/* Bow tie: formal --------------------------------------------------------- */
export function bowtie(kli) {
  const g = new THREE.Group();
  g.position.set(0, -0.4, 1.02);
  g.rotation.x = -0.45;
  for (const s of [-1, 1]) {
    const wing = mesh(new THREE.ConeGeometry(0.17, 0.3, 4), M.ink, [s * 0.15, 0, 0], [0, 0, s * Math.PI / 2]);
    wing.scale.z = 0.45;
    g.add(wing);
  }
  g.add(mesh(new THREE.SphereGeometry(0.07, 16, 12), M.blue));
  kli.head.add(g);
  return piece([g]);
}

/* Paper plane orbiting: contact ------------------------------------------- */
export function paperPlane(kli) {
  const geo = new THREE.BufferGeometry();
  const v = [0, 0, 0.5, -0.38, 0.04, -0.3, 0, 0, -0.3, /**/ 0, 0, 0.5, 0, 0, -0.3, 0.38, 0.04, -0.3, /**/ 0, 0, 0.5, 0, -0.13, -0.3, 0, 0, -0.3];
  geo.setAttribute('position', new THREE.Float32BufferAttribute(v, 3));
  geo.computeVertexNormals();
  const plane = mesh(geo, new THREE.MeshStandardMaterial({ color: '#fff', roughness: 0.6, side: THREE.DoubleSide, flatShading: true }));
  plane.scale.setScalar(0.75);
  const g = new THREE.Group();
  g.add(plane);
  kli.stage.add(g);
  const at = (a, out) => out.set(Math.cos(a) * 1.75, 0.55 + Math.sin(a * 2) * 0.22, Math.sin(a) * 1.25);
  const p0 = new THREE.Vector3(), p1 = new THREE.Vector3();
  let a = 0, speed = 0.7;
  return piece([g], (dt) => {
    speed += (0.7 - speed) * dt;
    a -= dt * speed;
    at(a, p0); at(a - 0.05, p1);
    plane.position.copy(p0);
    plane.lookAt(p1);
    plane.rotateZ(0.35);   // banking into the turn
  }, () => { speed = 4; });
}

/* Balloon: back to top ----------------------------------------------------- */
export function balloon(kli, { x = 0.95, y = 1.7 } = {}) {
  const g = new THREE.Group();
  const ball = new THREE.Group();
  const skin = mesh(new THREE.SphereGeometry(0.42, 32, 24), mat(C.blue, { coat: 1, rough: 0.2 }));
  skin.scale.y = 1.18;
  ball.add(skin);
  ball.add(mesh(new THREE.ConeGeometry(0.07, 0.1, 12), M.blue, [0, -0.52, 0], [Math.PI, 0, 0]));
  g.add(ball);
  const pts = new Float32Array(3 * 12);
  const line = new THREE.Line(new THREE.BufferGeometry(), new THREE.LineBasicMaterial({ color: C.ink }));
  line.geometry.setAttribute('position', new THREE.BufferAttribute(pts, 3));
  g.add(line);
  kli.stage.add(g);
  const anchor = new THREE.Vector3(0.82, -0.25, 0.4);
  return piece([g], (dt, t) => {
    ball.position.set(x + Math.sin(t * 1.1) * 0.12, y + Math.sin(t * 1.7) * 0.08 + kli.hop * 0.8, 0);
    ball.rotation.z = -Math.sin(t * 1.1) * 0.12;
    const knot = ball.position.clone().add(new THREE.Vector3(0, -0.58, 0));
    for (let i = 0; i < 12; i++) {
      const s = i / 11;
      const p = knot.clone().lerp(anchor, s);
      p.x += Math.sin(s * Math.PI) * Math.sin(t * 2 + s * 4) * 0.06;
      pts.set([p.x, p.y, p.z], i * 3);
    }
    line.geometry.attributes.position.needsUpdate = true;
  });
}
